import { LogoType } from "@/components/branding/logos";
import { Girdle } from "@/components/layout/Girdle";
import { NAVBAR_HEIGHT } from "@/components/layout/marketing-layout-constants";
import { MarketingScripts } from "@/utils/analytics/TrackingScripts";
import { AmplitudeEvent, AmplitudeScript, useAmplitudeOnClick } from "@/utils/amplitude";
import {
  GOOGLE_ANALYTICS_SITE_VERIFICATION_ID,
  GOOGLE_SEARCH_CONSOLE_SITE_VERIFICATION_ID,
} from "@/utils/analytics/google-site-verification";
import { GOOGLE_TAG_MANAGER_CONTAINER_ID } from "@/utils/analytics/google-tag-manager";
import { useAmplitudePageview } from "@/utils/hooks/use-amplitude-pageview";
import {
  canonicalUrlIsProduction,
  useCurrentCanonicalUrl,
} from "@/utils/hooks/url/use-current-url";
import { allThemeBGs } from "@/utils/marketing/colorThemes";
import { Routes } from "@/utils/routes";
import { GoogleTagManager } from "@next/third-parties/google";
import Head from "next/head";
import { ChalkLink } from "@/components/interactive/ChalkLink";
import React, { useEffect, useLayoutEffect, useMemo } from "react";

interface Props {
  children: React.ReactNode;
  title?: string;
  description?: string;
  keywords?: string;
  metaImage?: string;
}

export function SplitLayout({ children, title, description, keywords, metaImage }: Props) {
  // Apply scrollbar-gutter style to html element before paint to avoid layout shift
  useLayoutEffect(() => {
    document.documentElement.setAttribute("data-split-layout", "");
    return () => {
      document.documentElement.removeAttribute("data-split-layout");
    };
  }, []);

  useEffect(() => {
    // document?.body?.classList.add("bg-primary-green-300");
    document?.body?.classList.remove(...allThemeBGs);
    document?.body?.classList.add("bg-white");
  });

  useAmplitudePageview(AmplitudeEvent.VIEW_MARKETING_PAGE);

  const logoOnClick = useAmplitudeOnClick({
    event: AmplitudeEvent.MARKETING_CLICK,
    href: Routes.home,
    description: "Marketing split layout navbar logo",
  });

  const currentUrl = useCurrentCanonicalUrl();
  const urlIsProduction = useMemo(() => canonicalUrlIsProduction(currentUrl), [currentUrl]);

  const renderedTitle = title || "Chalk";

  return (
    <div className="relative min-h-screen bg-white flex flex-col">
      <Head>
        <title>{`${renderedTitle}`}</title>
        <meta charSet="utf-8" />
        <meta name="viewport" content="initial-scale=1.0, width=device-width" />
        <meta property="og:image" content={metaImage ?? "https://chalk.ai/opengraph.png"} />
        <meta property="og:title" content={`${renderedTitle}`} />
        <meta name="twitter:card" content="summary_large_image" />
        {description && <meta name="description" content={description} />}
        {description && <meta property="og:description" content={description} />}
        {keywords && <meta name="keywords" content={keywords} />}
        <meta name="google-site-verification" content={GOOGLE_ANALYTICS_SITE_VERIFICATION_ID} />
        <meta
          name="google-site-verification"
          content={GOOGLE_SEARCH_CONSOLE_SITE_VERIFICATION_ID}
        />
        <link rel="shortcut icon" href="/static/chalk.png" />
        <link rel="canonical" href={currentUrl} />
        {urlIsProduction ? (
          <meta name={"robots"} content={"index,follow"} />
        ) : (
          <meta name={"robots"} content={"noindex,nofollow"} />
        )}
        <AmplitudeScript />
      </Head>
      <GoogleTagManager gtmId={GOOGLE_TAG_MANAGER_CONTAINER_ID} />

      {urlIsProduction && <MarketingScripts />}

      <div className={"absolute top-0 z-20 isolate w-full"}>
        <Girdle disableMinWidth className={"w-full max-w-7xl px-4 sm:px-6 md:px-8 lg:px-10"}>
          <div className={"flex items-center w-full"} style={{ height: NAVBAR_HEIGHT }}>
            <div className={"h-full flex items-center"}>
              <ChalkLink href={Routes.home} legacyBehavior unstyled>
                <span onClick={() => logoOnClick()} className={"bg-marketing-darkgreen-bg"}>
                  <LogoType size={"lg"} color={"white"} className="cursor-pointer" />
                </span>
              </ChalkLink>
            </div>
          </div>
        </Girdle>
      </div>
      {children}
    </div>
  );
}
